/**
 * AI Legal Document Drafter - Unified Entry Version
 * 
 * Changes:
 * - Removed multi-step wizard (type, jurisdiction selection)
 * - AI automatically determines document type and jurisdiction
 * - Uses Tiptap rich text editor
 * - Three-column layout: Editor (50%) + AI Chat (35%) + RAG Panel (15%)
 * - Supports document template upload, AI structure analysis
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTranslation } from '../hooks/useTranslation';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
// Enhanced Table Extensions
import { TableKit, TableHandleExtension, TableTriggerButton } from './tiptap-node/table-node';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import { TextStyle } from '@tiptap/extension-text-style';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { SlashCommand } from '../extensions/SlashCommand';
import { LineHeight } from '../extensions/LineHeight';
import DragHandleWrapper from './editor/DragHandleWrapper';
import { BlockquoteButton } from './tiptap-ui/blockquote-button';
import { WorkflowStepCard, type WorkflowStep, type WorkflowPhaseInfo } from './workflow';
import './tiptap-node/list-node/list-node.scss';
import './tiptap-node/blockquote-node/blockquote-node.scss';
import './tiptap-node/heading-node/heading-node.scss';
import './tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import './tiptap-node/paragraph-node/paragraph-node.scss';
import './tiptap-node/image-node/image-node.scss';
import './tiptap-node/image-upload-node/image-upload-node.scss';
import './tiptap-node/table-node/styles/prosemirror-table.scss';
import './tiptap-node/table-node/styles/table-node.scss';
import {
    FileText,
    MessageSquare,
    User,
    Bot,
    Upload,
    Clipboard,
    Download,
    Trash2,
    Loader2,
    File as FileIcon,
    FileCode,
    Globe,
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    TextQuote,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Undo2,
    Redo2,
    Link as LinkIcon,
    Unlink,
    Table as TableIcon,
    Minus,
    Highlighter,
    MoreHorizontal,
    ChevronDown,
    Type,
    Paperclip,
    ArrowUp,
    Plus,
    AlignVerticalSpaceBetween,
    CheckCircle,
    CheckSquare,
    ListTodo,
    Square,
    History,
    Save,
    LogOut,
    FilePlus,
    X,
    ChevronLeft
} from 'lucide-react';

// Document export libraries
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ToolHistoryApiService } from '../services/api';
import { readFileByPath, readFileAsBase64 } from '../hooks/useFileSystem';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Chat Message Type - Supports Workflow Display
interface ChatMessage {
    id: string;
    role: 'user' | 'ai' | 'workflow';
    content: string;
    timestamp: number;
    // Workflow related - embedded in message
    workflowState?: {
        steps: WorkflowStep[];
        isRunning: boolean;
        reviewStatus?: 'pending' | 'accepted' | 'rejected';
        // Enhanced fields from Dynamic Orchestrator
        phase?: 'orchestrating' | 'researching' | 'drafting' | 'reviewing' | 'improving';
        phaseLabel?: string;
        activeWorkers?: string[];
        progress?: number;
    };
    // Attachment info - for rendering
    attachments?: { name: string; type: string }[];
    // Review: Legacy step structure (kept for compatibility until full migration)
    workflowStep?: {
        type: 'step' | 'tool_call' | 'tool_result' | 'content' | 'complete' | 'error';
        agentName?: string;
        stepNumber?: number;
        toolName?: string;
        status?: 'running' | 'completed' | 'error';
    };
}

// Uploaded template file
interface UploadedTemplate {
    file: File;
    name: string;
    content: string;
    structure?: {
        sections: string[];
        variables: string[];
    };
}

// Document session type - used for saving/restoring editor state
interface DraftSession {
    id: string;
    title: string;
    documentContent: string;  // Editor HTML content
    chatMessages: ChatMessage[];
    createdAt: string;
    updatedAt: string;
}

interface LegalDrafterProps {
    isOpen: boolean;
    onClose: () => void;
    // Unified entry: files and prompt passed from main chat
    initialFile?: File | null;
    initialPrompt?: string;
    // 从侧边栏打开历史草稿时传入sessionId
    initialSessionId?: string | null;
    // Force new draft counter - incremented when AI Draft button clicked
    forceNewDraft?: number;
    // External RAG component
    ragPanelComponent?: React.ReactNode;
    isRagPanelVisible?: boolean;
    onRagPanelToggle?: () => void;
    // History save callback
    onHistorySave?: (data: { title: string; summary: string; inputData?: object; outputData?: object }) => void;
    currentLanguage?: string;
    // ── File context props (System 1 data sources) ──
    localDirHandle?: FileSystemDirectoryHandle | null;
    localFileTreeListing?: string;
    caseFolders?: Array<{ id: string; name: string; enabled: boolean; files: Array<{ id: string; name: string; type: string }> }>;
    mentionFiles?: Array<{ id: string; name: string; source: string; folderName: string }>;
}

export const LegalDrafter: React.FC<LegalDrafterProps> = ({
    isOpen,
    onClose,
    initialFile,
    initialPrompt,
    initialSessionId,
    forceNewDraft,
    ragPanelComponent,
    isRagPanelVisible = true,
    onRagPanelToggle,
    onHistorySave,
    currentLanguage = 'zh-CN',
    localDirHandle,
    localFileTreeListing,
    caseFolders,
    mentionFiles: mentionFilesProp,
}) => {
    // Translation hook
    const { t } = useTranslation(currentLanguage);

    // === State Management ===
    const [uploadedTemplate, setUploadedTemplate] = useState<UploadedTemplate | null>(null);
    // Removed isDragging state as editor drag-and-drop is disabled

    // AI Chat State
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStatus, setGenerationStatus] = useState('');
    const chatContainerRef = useRef<HTMLDivElement>(null);

    // Export State
    const [isExporting, setIsExporting] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showHeadingMenu, setShowHeadingMenu] = useState(false);
    const [showListMenu, setShowListMenu] = useState(false);
    const [showAlignMenu, setShowAlignMenu] = useState(false);

    const [showLineHeightMenu, setShowLineHeightMenu] = useState(false);
    const [currentLineHeight, setCurrentLineHeight] = useState('1.5');
    const [workflowRunning, setWorkflowRunning] = useState(false);
    const [isAgentMode, setIsAgentMode] = useState(true); // Default to Agent mode

    // Unified workflow state - merge all steps into a single card
    // Unified workflow state - moving towards ChatMessage.workflowState
    // const [activeWorkflow, setActiveWorkflow] = useState<{...} | null>(null);
    const [preWorkflowContent, setPreWorkflowContent] = useState<string | null>(null);
    const [showLinkPopover, setShowLinkPopover] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);

    // @Mention autocomplete state
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0);
    const chatInputRef = useRef<HTMLInputElement>(null);
    const exportEditorRef = useRef<HTMLDivElement>(null);

    // Internal context references (from editor drag and drop)
    interface InternalContextReference {
        id: string;
        text: string;
        from: number;
        to: number;
        origin: 'editor-internal';
    }
    const [internalReferences, setInternalReferences] = useState<InternalContextReference[]>([]);

    // Attachment upload state
    const [chatAttachments, setChatAttachments] = useState<File[]>([]);
    const attachmentInputRef = useRef<HTMLInputElement>(null);

    // Workflow Abort Controller
    const workflowAbortController = useRef<AbortController | null>(null);

    // Initialization flag
    const hasAutoStarted = useRef(false);

    // Session management state
    const [sessions, setSessions] = useState<DraftSession[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [showHistoryMenu, setShowHistoryMenu] = useState(false);

    // Tiptap Editor

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Placeholder.configure({
                placeholder: t('draftPlaceholder'),
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    class: 'text-blue-600 underline cursor-pointer hover:text-blue-800',
                },
            }).extend({
                // Exit link when pressing right arrow at end or typing after link
                inclusive: false, // Don't include new text in link
            }),
            // Enhanced Table with resizable columns and handles
            TableKit.configure({
                table: {
                    resizable: true,
                },
            }),
            TableHandleExtension,
            Highlight.configure({
                multicolor: true,
            }),
            TextStyle,
            FontFamily.configure({
                types: ['textStyle'],
            }),
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            SlashCommand,
            LineHeight.configure({
                types: ['paragraph', 'heading', 'listItem', 'taskItem'],
            }),
        ],
        content: '',
        editorProps: {
            attributes: {
                class: 'tiptap-editor-content',
            },
        },
    });

    // Generate intelligent title from content
    const generateSmartTitle = useCallback((): string => {
        // 1. Use template name if available
        if (uploadedTemplate?.name) {
            return uploadedTemplate.name.replace(/\.(docx?|pdf|txt)$/i, '');
        }

        // 2. Try to extract title from document (H1 or H2)
        if (editor) {
            const html = editor.getHTML();
            const h1Match = html.match(/<h1[^>]*>([^<]+)<\/h1>/i);
            if (h1Match && h1Match[1].trim()) {
                return h1Match[1].trim().slice(0, 40);
            }
            const h2Match = html.match(/<h2[^>]*>([^<]+)<\/h2>/i);
            if (h2Match && h2Match[1].trim()) {
                return h2Match[1].trim().slice(0, 40);
            }

            // 3. Use first meaningful text from document
            const docText = editor.getText().trim();
            if (docText.length > 0) {
                // Clean up, take a reasonable slice for CJK or Latin
                const cleanText = docText.replace(/[\n\r]+/g, ' ').trim();
                const isCJK = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]/.test(cleanText);
                const maxLen = isCJK ? 20 : 40;
                return cleanText.slice(0, maxLen) + (cleanText.length > maxLen ? '...' : '');
            }
        }

        // 4. Use first user message as title
        const firstUserMessage = chatMessages.find(m => m.role === 'user');
        if (firstUserMessage?.content) {
            const content = firstUserMessage.content.trim();
            if (content.length > 0) {
                const isCJK = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]/.test(content);
                const maxLen = isCJK ? 15 : 30;
                return content.slice(0, maxLen) + (content.length > maxLen ? '...' : '');
            }
        }

        // 5. Default to localized date
        const now = new Date();
        const isChinese = currentLanguage?.startsWith('zh');
        if (isChinese) {
            return `${now.getMonth() + 1}月${now.getDate()}日 文档`;
        }
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${monthNames[now.getMonth()]} ${now.getDate()} Document`;
    }, [uploadedTemplate, editor, chatMessages, currentLanguage]);

    // Load session list
    const loadSessions = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tool-history?type=draft`, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.records) {
                    // Convert to DraftSession format
                    const draftSessions: DraftSession[] = data.records.map((r: { id: string; title: string; createdAt: string; updatedAt?: string }) => ({
                        id: r.id,
                        title: r.title || 'Untitled Document',
                        documentContent: '',
                        chatMessages: [],
                        createdAt: r.createdAt,
                        updatedAt: r.updatedAt || r.createdAt
                    }));
                    setSessions(draftSessions);
                }
            }
        } catch (error) {
            console.error('Failed to load session list:', error);
        }
    }, []);

    // Save current session - distinguish new vs update
    const saveCurrentSession = useCallback(async () => {
        if (!editor) return;

        const documentContent = editor.getHTML();
        const textSummary = editor.getText()?.slice(0, 200) || '';
        
        // Generate title: prefer AI for meaningful content, fallback to smart local title
        let title = generateSmartTitle();
        try {
            const contentForTitle = textSummary || chatMessages.find(m => m.role === 'user')?.content || '';
            // Only call AI title generation when there's substantial content (10+ chars)
            if (contentForTitle.trim().length >= 10) {
                const response = await fetch(`${API_BASE_URL}/api/chat/generate-title`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: `[Legal Draft] ${contentForTitle}` }),
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.title) {
                        const aiTitle = data.title.trim();
                        // Reject generic AI titles (date-based or "文书起草" patterns)
                        const isGeneric = /^(文书起草|文档|Document|Draft|Untitled|新文档|新建文档)[\s\d/月日]*$/i.test(aiTitle);
                        if (!isGeneric && aiTitle.length > 0) {
                            title = aiTitle;
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('AI title generation failed, using fallback:', e);
        }

        const saveData = {
            title,
            summary: textSummary || 'No content',
            inputData: {
                chatMessages: chatMessages.map(m => ({
                    id: m.id,
                    role: m.role,
                    content: m.content?.slice(0, 500),
                    timestamp: m.timestamp
                }))
            },
            outputData: {
                documentContent: documentContent.slice(0, 10000),
                templateName: uploadedTemplate?.name
            }
        };

        try {
            if (currentSessionId) {
                // Update existing record
                await ToolHistoryApiService.updateRecord(currentSessionId, saveData);
                console.log('Update session:', currentSessionId);
            } else {
                // Create new record
                const result = await ToolHistoryApiService.createRecord({
                    toolType: 'draft',
                    ...saveData
                });
                // Save new created ID
                if (result.record?.id) {
                    setCurrentSessionId(result.record.id);
                }
                console.log('Create new session:', result.record?.id);
            }
            // Refresh session list
            loadSessions();
        } catch (error) {
            console.error('Failed to save session:', error);
        }
    }, [editor, chatMessages, uploadedTemplate, loadSessions, generateSmartTitle, currentSessionId]);

    // Save session to history on close
    const handleClose = useCallback(async () => {
        // Save to history if there's chat content or editor content
        if (chatMessages.length > 0 || (editor && editor.getText().trim())) {
            await saveCurrentSession();
        }

        // Call original close callback
        onClose();
    }, [chatMessages, editor, saveCurrentSession, onClose]);

    // Create new session
    const createNewSession = useCallback(async () => {
        // Save current session first (if content exists)
        if (chatMessages.length > 0 || (editor && editor.getText().trim())) {
            await saveCurrentSession();
        }

        // Clear editor and chat
        editor?.commands.clearContent();
        setChatMessages([]);
        setUploadedTemplate(null);
        setCurrentSessionId(null);
        setShowHistoryMenu(false);
    }, [chatMessages, editor, saveCurrentSession]);

    // Load specific session
    const loadSession = useCallback(async (sessionId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tool-history/${sessionId}`, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.record) {
                    // Restore editor content
                    if (data.record.outputData?.documentContent) {
                        editor?.commands.setContent(data.record.outputData.documentContent);
                    }
                    // Restore chat messages
                    if (data.record.inputData?.chatMessages) {
                        setChatMessages(data.record.inputData.chatMessages);
                    }
                    setCurrentSessionId(sessionId);
                }
            }
        } catch (error) {
            console.error('Failed to load session:', error);
        }
        setShowHistoryMenu(false);
    }, [editor]);

    // Load session list on component open
    useEffect(() => {
        if (isOpen) {
            loadSessions();
        }
    }, [isOpen, loadSessions]);

    // 从侧边栏打开历史草稿: auto-load specific session
    useEffect(() => {
        if (isOpen && initialSessionId && initialSessionId !== currentSessionId) {
            loadSession(initialSessionId);
        }
    }, [isOpen, initialSessionId]);

    // Force new draft: when AI Draft button is clicked, auto-save current & start fresh
    const forceNewDraftRef = useRef(forceNewDraft);
    useEffect(() => {
        if (forceNewDraft !== undefined && forceNewDraft !== forceNewDraftRef.current) {
            forceNewDraftRef.current = forceNewDraft;
            if (isOpen) {
                // Auto-save current content if exists, then clear
                createNewSession();
            }
        }
    }, [forceNewDraft, isOpen, createNewSession]);

    // Scroll to latest message
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages]);

    // Handle initial file - template upload removed
    useEffect(() => {
        if (initialFile && isOpen && !hasAutoStarted.current) {
            hasAutoStarted.current = true;
            if (initialPrompt) {
                setTimeout(() => {
                    setChatInput(initialPrompt);
                }, 100);
            }
        }
    }, [initialFile, initialPrompt, isOpen]);

    // Reset state
    useEffect(() => {
        if (!isOpen) {
            hasAutoStarted.current = false;
        }
    }, [isOpen]);

    // Close export menu on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.export-dropdown-container') && !target.closest('.download-dropdown')) {
                setShowExportMenu(false);
            }
        };
        if (showExportMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showExportMenu]);

    // Validate file type
    const isValidFileType = (file: File): boolean => {
        const validTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
        ];
        return validTypes.includes(file.type) ||
            file.name.endsWith('.pdf') ||
            file.name.endsWith('.doc') ||
            file.name.endsWith('.docx') ||
            file.name.endsWith('.txt');
    };

    // Drag and drop removed - now only supports upload via chat box or right resource panel
    // Template upload removed - no longer used

    // Send message to AI
    const sendChatMessage = async () => {
        if (!chatInput.trim() && chatAttachments.length === 0) return;
        if (isChatLoading || workflowRunning) return;

        if (isAgentMode) {
            // Agent Mode: Use workflow processing
            await executeInlineWorkflow();
        } else {
            // Normal chat mode
            await sendNormalChat();
        }
    };

    // Normal chat mode
    const sendNormalChat = async () => {
        // Save attachment info for display
        const attachmentInfoForDisplay = chatAttachments.map(f => ({ name: f.name, type: f.type }));

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: chatInput,
            timestamp: Date.now(),
            attachments: attachmentInfoForDisplay.length > 0 ? attachmentInfoForDisplay : undefined,
        };
        setChatMessages(prev => [...prev, userMessage]);
        const currentInput = chatInput;
        setChatInput('');
        setChatAttachments([]);
        setIsChatLoading(true);

        const aiMessageId = (Date.now() + 1).toString();
        setChatMessages(prev => [...prev, {
            id: aiMessageId,
            role: 'ai',
            content: '',
            timestamp: Date.now(),
        }]);

        let contextMessage = currentInput;


        // Read text attachment content (chip reference)
        if (chatAttachments.length > 0) {
            for (const file of chatAttachments) {
                if (file.type === 'text/plain') {
                    // Check if this file corresponds to an internal reference
                    // The filename logic must match what we created in onDrop (refactoring to be more robust is better, but using filename matching for now)
                    const matchingRef = internalReferences.find(ref => file.name.includes(ref.text.slice(0, 10).replace(/[\n\r]/g, ' ')));

                    try {
                        const text = await file.text();

                        if (matchingRef) {
                            // Inject STRICT context for internal references
                            contextMessage += `\n\n[CRITICAL CONTEXT INSTRUCTION]\nThe user is explicitly referring to the following text block from the document:\n"${text}"\n\nThis block is located at position ${matchingRef.from} to ${matchingRef.to} in the document.\nAny changes or analysis MUST be applied specifically to this location/content.\n------------------------\n`;
                        } else {
                            contextMessage += `\n\nCited content (${file.name}):\n${text}`;
                        }
                    } catch (e) {
                        console.error('Failed to read attachment', e);
                    }
                }
            }
        }

        // Clear internal references after use (same as attachments)
        setInternalReferences([]);

        try {
            const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: contextMessage,
                    context: editor?.getHTML() || '',
                }),
            });

            if (!response.ok) throw new Error('Request failed');

            const reader = response.body?.getReader();
            if (reader) await processStream(reader, aiMessageId);
        } catch (err) {
            setChatMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, content: `❌ Error: ${err instanceof Error ? err.message : 'Request failed'}` }
                    : msg
            ));
        } finally {
            setIsChatLoading(false);
        }
    };

    // Process stream response
    const processStream = async (reader: ReadableStreamDefaultReader<Uint8Array>, aiMessageId: string) => {
        const decoder = new TextDecoder();
        let aiContent = '';
        let generatedDocument = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value);
            const lines = text.split('\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));

                        if (data.type === 'document') {
                            // Generated document content
                            generatedDocument += data.content || '';
                            if (editor && generatedDocument) {
                                editor.commands.setContent(generatedDocument);
                            }
                        } else if (data.content) {
                            // Normal chat content
                            aiContent += data.content;
                            setChatMessages(prev => prev.map(msg =>
                                msg.id === aiMessageId
                                    ? { ...msg, content: aiContent }
                                    : msg
                            ));
                        } else if (data.text) {
                            // Compatible with other formats
                            aiContent += data.text;
                            setChatMessages(prev => prev.map(msg =>
                                msg.id === aiMessageId
                                    ? { ...msg, content: aiContent }
                                    : msg
                            ));
                        }
                    } catch (e) {
                        // Ignore parse error
                    }
                }
            }
        }
    };

    // Generate document
    const generateDocument = async () => {
        if (!chatInput.trim() && !uploadedTemplate) return;

        setIsGenerating(true);
        setGenerationStatus(t('generating'));

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: chatInput || 'Please generate document based on template',
            timestamp: Date.now(),
        };
        setChatMessages(prev => [...prev, userMessage]);
        setChatInput('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/drafting/generate/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: chatInput || 'Generate complete document based on template',
                    templateContent: uploadedTemplate?.content || '',
                    autoDetect: true,
                }),
            });

            if (!response.ok) throw new Error('Generation request failed');

            const reader = response.body?.getReader();
            if (!reader) throw new Error('Cannot read response');

            const decoder = new TextDecoder();
            let documentContent = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const text = decoder.decode(value);
                const lines = text.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.slice(6));
                            if (event.type === 'chunk' || event.content) {
                                documentContent += event.content || event.text || '';
                                if (editor) {
                                    editor.commands.setContent(documentContent);
                                }
                            } else if (event.type === 'status') {
                                setGenerationStatus(event.message || event.data?.message || t('generating'));
                            }
                        } catch (e) { }
                    }
                }
            }

            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'ai',
                content: t('generatedSuccess'),
                timestamp: Date.now(),
            }]);

            // Save to tool history
            if (onHistorySave) {
                onHistorySave({
                    title: `Drafting: ${generateSmartTitle()}`,
                    summary: documentContent.slice(0, 200) + '...',
                    inputData: { prompt: chatInput || 'Generate document based on template' },
                    outputData: { contentLength: documentContent.length },
                });
            }
        } catch (error) {
            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'ai',
                content: `❌ Generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                timestamp: Date.now(),
            }]);
        } finally {
            setIsGenerating(false);
            setGenerationStatus('');
        }
    };

    // Execute inline workflow - unified step management (Cursor-like)
    const executeInlineWorkflow = async () => {
        if (!chatInput.trim() && chatAttachments.length === 0) return;
        if (workflowRunning) return;

        // Simple greeting detection - send to normal chat if it's simple greeting and no attachments
        const simpleGreetings = ['hi', 'hello', 'hey', '你好', '您好', '嗨', '在吗', '在不在', '测试'];
        const isSimpleGreeting = simpleGreetings.includes(chatInput.trim().toLowerCase()) && chatAttachments.length === 0;

        if (isSimpleGreeting) {
            await sendNormalChat();
            return;
        }

        // Save attachment info for display
        const attachmentInfoForDisplay = chatAttachments.map(f => ({ name: f.name, type: f.type }));


        // Save current editor content for potential undo/rollback
        if (editor) {
            setPreWorkflowContent(editor.getHTML());
        }

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: chatInput,
            timestamp: Date.now(),
            attachments: attachmentInfoForDisplay.length > 0 ? attachmentInfoForDisplay : undefined,
        };
        setChatMessages(prev => [...prev, userMessage]);
        const currentInput = chatInput;
        const currentAttachments = [...chatAttachments];
        const currentInternalRefs = [...internalReferences]; // Capture current references

        setChatInput('');
        setChatAttachments([]);
        setInternalReferences([]); // Clear references
        setWorkflowRunning(true);

        // Generate workflow ID
        const workflowId = `wf-${Date.now()}`;

        // Initialize workflow message (AI role, with workflow state)
        const workflowMessage: ChatMessage = {
            id: workflowId,
            role: 'ai',
            content: '', // Empty content, mainly for displaying workflow state
            timestamp: Date.now(),
            workflowState: {
                steps: [],
                isRunning: true
            }
        };

        setChatMessages(prev => [...prev, workflowMessage]);

        try {
            // If attachments, parse attachment content first
            let templateContent = uploadedTemplate?.content || '';

            // Include current editor content as context, support "continue" scenario
            if (editor && editor.getText().trim()) {
                const currentText = editor.getText();
                // If no template uploaded or content changed significantly, treat current content as new context base
                if (!templateContent || currentText.length > templateContent.length + 100) {
                    // Smart merge: if seems modification on existing template, keep; otherwise supplement
                    templateContent = `[Current Document Content]:\n${currentText}\n\n[Original Template/Context]:\n${templateContent}`;
                }
            }

            let citationContent = ''; // Citation content (local read)

            if (currentAttachments.length > 0) {
                // Separate local text files and files needing backend parsing
                const textFiles: File[] = [];
                const otherFiles: File[] = [];

                for (const file of currentAttachments) {
                    if (file.type === 'text/plain') {
                        textFiles.push(file);
                    } else {
                        otherFiles.push(file);
                    }
                }

                // Read local text files (cite chip)
                for (const file of textFiles) {
                    try {
                        const text = await file.text();

                        // Check for matching internal reference
                        const matchingRef = currentInternalRefs.find(ref => file.name.includes(ref.text.slice(0, 10).replace(/[\n\r]/g, ' ')));

                        if (matchingRef) {
                            citationContent += `\n\n[CRITICAL CONTEXT INSTRUCTION]\nThe user is explicitly referring to the following text block from the document:\n"${text}"\n\nThis block is located at position ${matchingRef.from} to ${matchingRef.to} in the document.\nAny changes or analysis MUST be applied specifically to this location/content.\n------------------------\n`;
                        } else {
                            citationContent += `\n\nCited content (${file.name}):\n${text}`;
                        }
                    } catch (e) {
                        console.error('Failed to read text file', e);
                    }
                }

                // Send other files to backend for parsing
                if (otherFiles.length > 0) {
                    const formData = new FormData();
                    otherFiles.forEach(file => formData.append('files', file));

                    try {
                        const parseResponse = await fetch(`${API_BASE_URL}/api/documents/parse-multiple`, {
                            method: 'POST',
                            body: formData,
                        });

                        if (parseResponse.ok) {
                            const parseResult = await parseResponse.json();
                            templateContent = parseResult.texts?.join('\n\n---\n\n') || templateContent;
                        }
                    } catch (e) {
                        console.log('Attachment parsing failed, continuing with user input');
                    }
                }
            }

            // Create AbortController for cancellation
            workflowAbortController.current = new AbortController();

            // ── @Mention file pre-reading (System 1: no tool_request, must send all data upfront) ──
            const mentionedFileContents: Record<string, string> = {};
            const mentionedFilePaths: string[] = [];

            // 1. Parse @[Local/...] mentions → pre-read local files
            const localRefs = currentInput.match(/@\[Local\/([^\]]+)\]/g) || [];
            for (const ref of localRefs) {
                const path = ref.slice(7, -1); // Remove @[Local/ and ]
                mentionedFilePaths.push(path);
                if (localDirHandle) {
                    try {
                        const fileData = await readFileAsBase64(localDirHandle, path);
                        if (fileData) {
                            const tkn = localStorage.getItem('auth_token');
                            const resp = await fetch(`${API_BASE_URL}/api/file-reader/extract`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    ...(tkn ? { 'Authorization': `Bearer ${tkn}` } : {}),
                                },
                                body: JSON.stringify({ base64: fileData.base64, fileName: fileData.fileName }),
                            });
                            if (resp.ok) {
                                const result = await resp.json();
                                if (result.success) {
                                    mentionedFileContents[path] = result.content;
                                }
                            }
                        }
                    } catch (e) {
                        console.warn(`[LegalDrafter] Failed to pre-read local file: ${path}`, e);
                    }
                }
            }

            // 2. Parse @[FolderName/FileName] mentions (case files) → record paths only, backend reads from R2
            const allMentionRefs = currentInput.match(/@\[([^\]]+)\]/g) || [];
            for (const ref of allMentionRefs) {
                const fullPath = ref.slice(2, -1); // Remove @[ and ]
                if (fullPath.startsWith('Local/')) continue; // Already handled above
                mentionedFilePaths.push(fullPath);
            }

            // 3. Dynamic file tree context trimming (consistent with chat.ts buildDynamicFileTreeContext)
            let fileTreeContext: string | undefined;
            if (localFileTreeListing) {
                const treeLines = localFileTreeListing.split('\n');
                const totalItems = treeLines.filter(l => l.trim()).length;
                if (localRefs.length > 0) {
                    // @mention mode: only nearby lines
                    const relevantLineIndices = new Set<number>();
                    for (const ref of localRefs) {
                        const fileName = ref.slice(7, -1).split('/').pop() || '';
                        for (let i = 0; i < treeLines.length; i++) {
                            if (treeLines[i].includes(fileName)) {
                                for (let j = Math.max(0, i - 2); j <= Math.min(treeLines.length - 1, i + 2); j++) {
                                    relevantLineIndices.add(j);
                                }
                            }
                        }
                    }
                    if (relevantLineIndices.size > 0) {
                        const sorted = Array.from(relevantLineIndices).sort((a, b) => a - b);
                        fileTreeContext = sorted.map(i => treeLines[i]).join('\n');
                    }
                } else {
                    const fullTreeKeywords = ['整理', '所有文件', '全部文件', '文件夹', '归类', '分类', 'organize', 'all files'];
                    if (fullTreeKeywords.some(kw => currentInput.toLowerCase().includes(kw))) {
                        fileTreeContext = localFileTreeListing;
                    } else if (totalItems > 0) {
                        fileTreeContext = `[Local folder mounted: ${totalItems} items]`;
                    }
                }
            }

            // Call workflow API
            // Build auth headers
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            const token = localStorage.getItem('auth_token');
            if (token) headers['Authorization'] = `Bearer ${token}`;
            try {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const user = JSON.parse(userStr);
                    if (user?.id) headers['x-user-id'] = user.id;
                }
            } catch (e) { }

            const response = await fetch(`${API_BASE_URL}/api/drafting/workflow/stream`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    documentType: 'general',  // Let AI determine document type from user request
                    country: 'AUTO',          // Auto-detect jurisdiction from user's input language
                    subject: currentInput,
                    parties: [
                        { role: 'Party A', name: 'TBD', type: 'individual' },
                        { role: 'Party B', name: 'TBD', type: 'individual' },
                    ],
                    customRequirements: currentInput + citationContent,
                    userUploadedTemplate: templateContent,
                    // Convert chatMessages to conversationHistory format for multi-turn context
                    conversationHistory: chatMessages
                        .filter(msg => msg.content && msg.content.trim()) // Filter empty messages
                        .filter(msg => !msg.workflowState) // Exclude workflow state messages (only keep actual content)
                        .slice(-10) // Keep last 10 messages to avoid token overflow
                        .map(msg => ({
                            role: msg.role === 'user' ? 'user' as const : 'assistant' as const,
                            content: msg.content,
                        })),
                    // File context (System 1 data sources)
                    fileTreeListing: fileTreeContext || undefined,
                    mentionedFileContents: Object.keys(mentionedFileContents).length > 0 ? mentionedFileContents : undefined,
                    mentionedFilePaths: mentionedFilePaths.length > 0 ? mentionedFilePaths : undefined,
                    enabledCaseFolderIds: caseFolders?.filter(f => f.enabled).map(f => f.id),
                    localDirMounted: !!localDirHandle,
                }),
                signal: workflowAbortController.current.signal,
            });

            if (!response.ok) {
                throw new Error(t('workflowStartFailed'));
            }

            // Workflow started, wait for backend steps return

            const reader = response.body?.getReader();
            if (!reader) throw new Error('Cannot read response stream');

            const decoder = new TextDecoder();
            let buffer = '';
            let isDraftingError = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.slice(6));
                            const stepId = `step-${event.type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

                            if (event.type === 'step') {
                                // Check if simple_chat type - detect if content includes simple_chat
                                const isSimpleChatStep = (event.content || '').includes('simple_chat');

                                if (isSimpleChatStep) {
                                    // For simple_chat, remove workflowState, don't show workflow card
                                    setChatMessages(prev => prev.map(msg => {
                                        if (msg.id !== workflowId) return msg;
                                        // Remove workflowState, only keep content
                                        const { workflowState, ...rest } = msg;
                                        return rest;
                                    }));
                                } else {
                                    // Main step — also capture orchestrator phase info
                                    setChatMessages(prev => prev.map(msg => {
                                        if (msg.id !== workflowId || !msg.workflowState) return msg;

                                        return {
                                            ...msg,
                                            workflowState: {
                                                ...msg.workflowState,
                                                steps: [
                                                    ...msg.workflowState.steps.map(s => s.status === 'running' ? { ...s, status: 'completed' as const } : s),
                                                    {
                                                        id: stepId,
                                                        content: event.content || event.agentName || 'Processing...',
                                                        status: 'running' as const,
                                                        type: 'main',
                                                        timestamp: Date.now(),
                                                    }
                                                ],
                                                // Capture orchestrator phase info for WorkflowStepCard
                                                ...(event.phase && { phase: event.phase }),
                                                ...(event.phaseLabel && { phaseLabel: event.phaseLabel }),
                                                ...(event.activeWorkers && { activeWorkers: event.activeWorkers }),
                                                ...(event.progress !== undefined && { progress: event.progress }),
                                            }
                                        };
                                    }));
                                }
                            } else if (event.type === 'tool_call') {
                                // Tool call as sub-step
                                setChatMessages(prev => prev.map(msg => {
                                    if (msg.id !== workflowId || !msg.workflowState) return msg;

                                    return {
                                        ...msg,
                                        workflowState: {
                                            ...msg.workflowState,
                                            steps: [
                                                ...msg.workflowState.steps,
                                                {
                                                    id: stepId,
                                                    content: `Tool Call: ${event.toolName}`,
                                                    status: 'running' as const,
                                                    type: 'tool',
                                                    timestamp: Date.now(),
                                                    toolName: event.toolName,
                                                }
                                            ]
                                        }
                                    };
                                }));

                                // Delay mark as completed (simulate tool execution)
                                setTimeout(() => {
                                    setChatMessages(prev => prev.map(msg => {
                                        if (msg.id !== workflowId || !msg.workflowState) return msg;

                                        return {
                                            ...msg,
                                            workflowState: {
                                                ...msg.workflowState,
                                                steps: msg.workflowState.steps.map(s =>
                                                    s.id === stepId ? { ...s, status: 'completed' as const } : s
                                                )
                                            }
                                        };
                                    }));
                                }, 500);
                            } else if (event.type === 'content') {
                                // Direct content based on agentName
                                if (event.agentName === 'chat') {
                                    // Simple chat content show in message content
                                    if (event.content) {
                                        setChatMessages(prev => prev.map(msg => {
                                            if (msg.id !== workflowId) return msg;
                                            return {
                                                ...msg,
                                                content: (msg.content || '') + event.content
                                            };
                                        }));
                                    }
                                } else if (event.agentName === 'research') {
                                    // Research content update to current running step
                                    if (event.content) {
                                        setChatMessages(prev => prev.map(msg => {
                                            if (msg.id !== workflowId || !msg.workflowState) return msg;

                                            return {
                                                ...msg,
                                                workflowState: {
                                                    ...msg.workflowState,
                                                    steps: msg.workflowState.steps.map(step => {
                                                        // Find current running main step (usually the last added one)
                                                        if (step.status === 'running') {
                                                            // Avoid duplicate newline
                                                            const prefix = step.content.endsWith('\n') ? '' : '\n';
                                                            return {
                                                                ...step,
                                                                content: step.content + prefix + event.content
                                                            };
                                                        }
                                                        return step;
                                                    })
                                                }
                                            };
                                        }));
                                    }
                                } else if (event.agentName === 'drafting' && event.content && editor) {
                                    // Check if error message (starts with specific words)
                                    // Only check at start, or if already marked as error
                                    const isErrorStart = event.content.startsWith('很抱歉') ||
                                        event.content.startsWith('Sorry') ||
                                        event.content.startsWith('Error') ||
                                        event.content.startsWith('无法') ||
                                        event.content.startsWith('抱歉');

                                    // Use backend-specified contentType if available
                                    // 'document' = write to editor, 'conversation' = show in chat
                                    const contentType = event.contentType || 'document'; // Default to document for backward compatibility

                                    if (isDraftingError || (event.content.length < 50 && isErrorStart) || contentType === 'conversation') {
                                        isDraftingError = isDraftingError || isErrorStart;
                                        // Error message or conversation show in chat, not write to editor
                                        setChatMessages(prev => prev.map(msg => {
                                            if (msg.id !== workflowId) return msg;
                                            return {
                                                ...msg,
                                                content: event.content // Show in chat panel
                                            };
                                        }));
                                    } else {
                                        // Document content - write to editor
                                        editor.commands.setContent(event.content);
                                    }
                                }
                            } else if (event.type === 'complete') {
                                // Workflow complete
                                setChatMessages(prev => prev.map(msg => {
                                    if (msg.id !== workflowId || !msg.workflowState) return msg;

                                    return {
                                        ...msg,
                                        workflowState: {
                                            ...msg.workflowState,
                                            isRunning: false,
                                            steps: [
                                                ...msg.workflowState.steps.map(s => s.status === 'running' ? { ...s, status: 'completed' as const } : s),
                                                {
                                                    id: stepId,
                                                    content: event.content || 'Drafting Completed!',
                                                    status: 'completed' as const,
                                                    type: 'main',
                                                    timestamp: Date.now(),
                                                }
                                            ]
                                        }
                                    };
                                }));

                                // Write final draft to editor
                                if (event.finalDraft && editor) {
                                    editor.commands.setContent(event.finalDraft);
                                }
                            } else if (event.type === 'error') {
                                // Error handling
                                setChatMessages(prev => prev.map(msg => {
                                    if (msg.id !== workflowId || !msg.workflowState) return msg;

                                    return {
                                        ...msg,
                                        content: msg.content + `\n❌ Error: ${event.error}`,
                                        workflowState: {
                                            ...msg.workflowState,
                                            isRunning: false,
                                            steps: [
                                                ...msg.workflowState.steps,
                                                {
                                                    id: stepId,
                                                    content: `Error: ${event.error}`,
                                                    status: 'error' as const,
                                                    type: 'main',
                                                    timestamp: Date.now(),
                                                }
                                            ]
                                        }
                                    };
                                }));
                            }
                        } catch (e) {
                            console.error('Failed to parse workflow event:', e);
                        }
                    }
                }
            }
        } catch (error) {
            // Error handling
            const isAborted = error instanceof Error && error.name === 'AbortError';

            setChatMessages(prev => prev.map(msg => {
                if (msg.id !== workflowId || !msg.workflowState) return msg;
                return {
                    ...msg,
                    workflowState: {
                        ...msg.workflowState,
                        isRunning: false,
                        // If paused, don't mark as error, just stopped
                        steps: isAborted
                            ? msg.workflowState.steps.map(s => s.status === 'running' ? { ...s, status: 'completed' as const, content: s.content + ' (Paused)' } : s)
                            : msg.workflowState.steps
                    }
                };
            }));

            if (!isAborted) {
                setChatMessages(prev => [...prev, {
                    id: Date.now().toString(),
                    role: 'ai',
                    content: `❌ ${t('workflowExecutionFailed')}: ${error instanceof Error ? error.message : t('unknownError')}`,
                    timestamp: Date.now(),
                }]);
            } else {
                setChatMessages(prev => [...prev, {
                    id: Date.now().toString(),
                    role: 'ai',
                    content: `⏸️ Workflow paused. You can command to continue or start a new task.`,
                    timestamp: Date.now(),
                }]);
            }
        } finally {
            setWorkflowRunning(false);
        }
    };


    // Handle attachment upload
    const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const newFiles = Array.from(files);
            setChatAttachments(prev => [...prev, ...newFiles]);
        }
        // Reset input to allow selecting the same file again
        e.target.value = '';
    };

    // Remove attachment
    const removeAttachment = (index: number) => {
        setChatAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Export document
    const exportDocument = async (format: 'doc' | 'pdf' | 'txt' | 'html') => {
        if (!editor || isExporting) return;

        const content = editor.getHTML();
        const textContent = editor.getText();

        // Check if content is empty
        if (!textContent.trim()) {
            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'ai',
                content: '⚠️ Editor content is empty, please input or generate document content first.',
                timestamp: Date.now(),
            }]);
            return;
        }

        setIsExporting(true);
        setShowExportMenu(false);

        try {
            const fileName = `Legal_Document_${new Date().toISOString().slice(0, 10)}`;

            // Wrap HTML to support fonts
            const wrappedHtml = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <style>
                        body {
                            font-family: 'Times New Roman', 'SimSun', 'Source Han Serif CN', 'Noto Serif CJK SC', serif;
                            font-size: 14px;
                            line-height: 1.8;
                            color: #333;
                            padding: 40px;
                        }
                        h1, h2, h3 { font-weight: bold; margin: 20px 0 10px; }
                        h1 { font-size: 24px; text-align: center; }
                        h2 { font-size: 18px; }
                        h3 { font-size: 16px; }
                        p { margin: 10px 0; text-indent: 2em; text-align: justify; }
                        ul, ol { margin: 10px 0; padding-left: 40px; }
                        li { margin: 5px 0; }
                    </style>
                </head>
                <body>${content}</body>
                </html>
            `;

            switch (format) {
                case 'doc': {
                    // Use docx library to generate Word document
                    // Parse HTML content to document elements
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = content;

                    const children: Paragraph[] = [];

                    // Traverse all child nodes
                    const processNode = (node: Node) => {
                        if (node.nodeType === Node.TEXT_NODE) {
                            const text = node.textContent?.trim();
                            if (text) {
                                children.push(new Paragraph({
                                    children: [new TextRun({ text, size: 24 })],
                                }));
                            }
                        } else if (node.nodeType === Node.ELEMENT_NODE) {
                            const el = node as HTMLElement;
                            const tagName = el.tagName.toLowerCase();
                            const text = el.textContent?.trim() || '';

                            if (tagName === 'h1') {
                                children.push(new Paragraph({
                                    heading: HeadingLevel.HEADING_1,
                                    children: [new TextRun({ text, bold: true, size: 36 })],
                                    alignment: AlignmentType.CENTER,
                                }));
                            } else if (tagName === 'h2') {
                                children.push(new Paragraph({
                                    heading: HeadingLevel.HEADING_2,
                                    children: [new TextRun({ text, bold: true, size: 32 })],
                                }));
                            } else if (tagName === 'h3') {
                                children.push(new Paragraph({
                                    heading: HeadingLevel.HEADING_3,
                                    children: [new TextRun({ text, bold: true, size: 28 })],
                                }));
                            } else if (tagName === 'p') {
                                children.push(new Paragraph({
                                    children: [new TextRun({ text, size: 24 })],
                                    spacing: { after: 200 },
                                }));
                            } else if (tagName === 'li') {
                                children.push(new Paragraph({
                                    children: [new TextRun({ text: `• ${text}`, size: 24 })],
                                    spacing: { after: 100 },
                                }));
                            } else if (tagName === 'blockquote') {
                                children.push(new Paragraph({
                                    children: [new TextRun({ text, italics: true, size: 24 })],
                                    indent: { left: 720 },
                                }));
                            } else {
                                // Recursively process child nodes
                                el.childNodes.forEach(child => processNode(child));
                            }
                        }
                    };

                    tempDiv.childNodes.forEach(child => processNode(child));

                    // If no content, add empty paragraph
                    if (children.length === 0) {
                        children.push(new Paragraph({
                            children: [new TextRun({ text: textContent, size: 24 })],
                        }));
                    }

                    const doc = new Document({
                        sections: [{
                            properties: {},
                            children: children,
                        }],
                    });

                    const blob = await Packer.toBlob(doc);
                    // Use native download approach for reliable filenames
                    const docxBlob = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                    const url = URL.createObjectURL(docxBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${fileName}.docx`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    break;
                }
                case 'pdf': {
                    // Create temporary render container
                    const tempContainer = document.createElement('div');
                    tempContainer.style.cssText = `
                        position: absolute;
                        left: -9999px;
                        top: 0;
                        width: 794px;
                        background: white;
                        padding: 40px;
                        font-family: 'Times New Roman', 'SimSun', serif;
                        font-size: 14px;
                        line-height: 1.8;
                    `;
                    tempContainer.innerHTML = content;
                    document.body.appendChild(tempContainer);

                    try {
                        // Use html2canvas to render
                        const canvas = await html2canvas(tempContainer, {
                            scale: 2,
                            useCORS: true,
                            logging: false,
                            backgroundColor: '#ffffff',
                        });

                        // Use jsPDF to generate PDF
                        const pdf = new jsPDF({
                            orientation: 'portrait',
                            unit: 'mm',
                            format: 'a4',
                        });

                        const imgWidth = 210; // A4 width mm
                        const imgHeight = (canvas.height * imgWidth) / canvas.width;
                        const pageHeight = 297; // A4 height mm

                        let heightLeft = imgHeight;
                        let position = 0;

                        // Add first page
                        pdf.addImage(
                            canvas.toDataURL('image/jpeg', 0.95),
                            'JPEG',
                            0,
                            position,
                            imgWidth,
                            imgHeight
                        );
                        heightLeft -= pageHeight;

                        // Add more pages
                        while (heightLeft > 0) {
                            position = heightLeft - imgHeight;
                            pdf.addPage();
                            pdf.addImage(
                                canvas.toDataURL('image/jpeg', 0.95),
                                'JPEG',
                                0,
                                position,
                                imgWidth,
                                imgHeight
                            );
                            heightLeft -= pageHeight;
                        }

                        pdf.save(`${fileName}.pdf`);
                    } finally {
                        document.body.removeChild(tempContainer);
                    }
                    break;
                }
                case 'html': {
                    const blob = new Blob([wrappedHtml], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${fileName}.html`;
                    a.click();
                    URL.revokeObjectURL(url);
                    break;
                }
                case 'txt': {
                    const blob = new Blob([textContent], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${fileName}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                    break;
                }
            }

            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'ai',
                content: `✅ Successfully exported as ${format.toUpperCase()} format!`,
                timestamp: Date.now(),
            }]);
        } catch (error) {
            console.error('Export error:', error);
            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'ai',
                content: `❌ Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                timestamp: Date.now(),
            }]);
        } finally {
            setIsExporting(false);
        }
    };


    // Copy to clipboard
    const copyToClipboard = () => {
        if (!editor) return;
        navigator.clipboard.writeText(editor.getText());
        setChatMessages(prev => [...prev, {
            id: Date.now().toString(),
            role: 'ai',
            content: 'Copied to clipboard!',
            timestamp: Date.now(),
        }]);
    };

    // Clear editor
    const clearEditor = () => {
        if (editor) {
            editor.commands.clearContent();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="legal-drafter-fullscreen">
            {/* Main Content - 3 Column Layout */}
            <div className={`drafter-content ${isRagPanelVisible && ragPanelComponent ? 'with-rag' : 'without-rag'}`}>
                {/* Left: Tiptap Editor - Notion Style Clean Interface */}
                <div className="editor-panel">
                    {/* Top Right Floating Controls */}
                    <div className="floating-controls">
                        {isGenerating && (
                            <span className="status-badge-compact"><Loader2 className="w-3 h-3 animate-spin" /> {generationStatus}</span>
                        )}
                    </div>

                    {/* Editor Toolbar - Tiptap Style */}
                    <div className="editor-toolbar-tiptap">
                        {/* Left Actions - Exit and New */}
                        <div className="toolbar-left-actions">
                            <button
                                className="tiptap-btn toolbar-action-btn exit-btn"
                                onClick={handleClose}
                                title={t('drafterExit')}
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                            <button
                                className="tiptap-btn toolbar-action-btn new-btn"
                                onClick={createNewSession}
                                title={t('drafterNewChat')}
                            >
                                <FilePlus className="w-4 h-4" />
                            </button>
                            <div className="toolbar-divider" />
                        </div>

                        {/* Heading Dropdown */}
                        <div className="dropdown-container">
                            <button
                                className={`tiptap-btn-text ${editor?.isActive('heading') ? 'active' : ''}`}
                                onClick={() => { setShowHeadingMenu(!showHeadingMenu); setShowListMenu(false); setShowAlignMenu(false); setShowLineHeightMenu(false); }}
                            >
                                <span>H</span>
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {showHeadingMenu && (
                                <div className="dropdown-menu">
                                    <div className="dropdown-item" onClick={() => { editor?.chain().focus().toggleHeading({ level: 1 }).run(); setShowHeadingMenu(false); }}>
                                        <span style={{ fontSize: '18px', fontWeight: 700 }}>Heading 1</span>
                                    </div>
                                    <div className="dropdown-item" onClick={() => { editor?.chain().focus().toggleHeading({ level: 2 }).run(); setShowHeadingMenu(false); }}>
                                        <span style={{ fontSize: '16px', fontWeight: 600 }}>Heading 2</span>
                                    </div>
                                    <div className="dropdown-item" onClick={() => { editor?.chain().focus().toggleHeading({ level: 3 }).run(); setShowHeadingMenu(false); }}>
                                        <span style={{ fontSize: '14px', fontWeight: 600 }}>Heading 3</span>
                                    </div>
                                    <div className="dropdown-divider" />
                                    <div className="dropdown-item" onClick={() => { editor?.chain().focus().setParagraph().run(); setShowHeadingMenu(false); }}>
                                        <span style={{ fontSize: '14px' }}>Paragraph</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Font dropdown removed - browser default font respects OS language settings */}

                        {/* Line Height Dropdown */}
                        <div className="dropdown-container">
                            <button
                                className="tiptap-btn"
                                onClick={() => { setShowLineHeightMenu(!showLineHeightMenu); setShowHeadingMenu(false); setShowListMenu(false); setShowAlignMenu(false); }}
                                title={t('drafterLineHeight')}
                            >
                                {/* Line height icon: A with vertical spacing arrows */}
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 19L9 5L15 19" />
                                    <path d="M5 13H13" />
                                    <path d="M20 5L20 19" />
                                    <path d="M17 8L20 5L23 8" />
                                    <path d="M17 16L20 19L23 16" />
                                </svg>
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {showLineHeightMenu && (
                                <div className="dropdown-menu">
                                    {['1.0', '1.25', '1.5', '1.75', '2.0', '2.5', '3.0'].map((height) => (
                                        <div
                                            key={height}
                                            className={`dropdown-item ${currentLineHeight === height ? 'active' : ''}`}
                                            onClick={() => {
                                                setCurrentLineHeight(height);
                                                // Use Tiptap extension to apply line height to selected paragraphs
                                                editor?.chain().focus().setLineHeight(height).run();
                                                setShowLineHeightMenu(false);
                                            }}
                                        >
                                            {height}{t('drafterLineHeightSuffix')}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="tiptap-divider" />

                        {/* List Dropdown - Tiptap ListDropdownMenu Style */}
                        <div className="dropdown-container list-dropdown">
                            <button
                                className={`tiptap-btn ${editor?.isActive('bulletList') || editor?.isActive('orderedList') || editor?.isActive('taskList') ? 'active' : ''}`}
                                onClick={() => { setShowListMenu(!showListMenu); setShowHeadingMenu(false); setShowAlignMenu(false); setShowLineHeightMenu(false); }}
                                aria-label={t('drafterList')}
                                aria-expanded={showListMenu}
                            >
                                {editor?.isActive('orderedList') ? <ListOrdered className="w-4 h-4" /> :
                                    editor?.isActive('taskList') ? <CheckSquare className="w-4 h-4" /> :
                                        <List className="w-4 h-4" />}
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {showListMenu && (
                                <div className="dropdown-menu list-dropdown-menu">
                                    <div className="list-button-group">
                                        <button
                                            className={`list-type-btn ${editor?.isActive('bulletList') ? 'active' : ''}`}
                                            onClick={() => { editor?.chain().focus().toggleBulletList().run(); setShowListMenu(false); }}
                                            title={`${t('drafterBulletList')} (⌘+Shift+8)`}
                                            aria-pressed={editor?.isActive('bulletList')}
                                        >
                                            <List className="w-4 h-4" />
                                        </button>
                                        <button
                                            className={`list-type-btn ${editor?.isActive('orderedList') ? 'active' : ''}`}
                                            onClick={() => { editor?.chain().focus().toggleOrderedList().run(); setShowListMenu(false); }}
                                            title={`${t('drafterOrderedList')} (⌘+Shift+7)`}
                                            aria-pressed={editor?.isActive('orderedList')}
                                        >
                                            <ListOrdered className="w-4 h-4" />
                                        </button>
                                        <button
                                            className={`list-type-btn ${editor?.isActive('taskList') ? 'active' : ''}`}
                                            onClick={() => { editor?.chain().focus().toggleTaskList().run(); setShowListMenu(false); }}
                                            title={`${t('drafterTaskList')} (⌘+Shift+9)`}
                                            aria-pressed={editor?.isActive('taskList')}
                                        >
                                            <CheckSquare className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Blockquote Shortcut - Use BlockquoteButton Component */}
                        <BlockquoteButton
                            editor={editor}
                            showShortcut={false}
                            onToggled={() => console.log('Blockquote toggled!')}
                        />

                        {/* Table */}
                        <TableTriggerButton editor={editor} />

                        <div className="tiptap-divider" />

                        {/* Text Formatting */}
                        <button
                            className={`tiptap-btn ${editor?.isActive('bold') ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                            disabled={!editor}
                            title={t('drafterBold')}
                        >
                            <Bold className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive('italic') ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                            disabled={!editor}
                            title={t('drafterItalic')}
                        >
                            <Italic className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive('strike') ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().toggleStrike().run()}
                            disabled={!editor}
                            title={t('drafterStrike')}
                        >
                            <Strikethrough className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive('underline') ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().toggleUnderline().run()}
                            disabled={!editor}
                            title={t('drafterUnderline')}
                        >
                            <UnderlineIcon className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive('highlight') ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().toggleHighlight().run()}
                            disabled={!editor}
                            title={t('drafterHighlight')}
                        >
                            <Highlighter className="w-4 h-4" />
                        </button>
                        {/* Link Button and Popover */}
                        <div className="dropdown-container">
                            <button
                                className={`tiptap-btn ${editor?.isActive('link') ? 'active' : ''}`}
                                onClick={() => {
                                    if (editor?.isActive('link')) {
                                        const currentHref = editor?.getAttributes('link').href || '';
                                        setLinkUrl(currentHref);
                                    } else {
                                        setLinkUrl('');
                                    }
                                    setShowLinkPopover(!showLinkPopover);
                                }}
                                disabled={!editor}
                                title={t('drafterLink')}
                            >
                                <LinkIcon className="w-4 h-4" />
                            </button>
                            {showLinkPopover && (
                                <div className="link-popover">
                                    <div className="link-popover-header">
                                        <LinkIcon className="w-5 h-5 link-icon" />
                                    </div>
                                    <div className="link-popover-input-row">
                                        <input
                                            type="text"
                                            placeholder="Paste a link..."
                                            value={linkUrl}
                                            onChange={(e) => setLinkUrl(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && linkUrl) {
                                                    editor?.chain().focus().setLink({ href: linkUrl }).run();
                                                    setShowLinkPopover(false);
                                                    setLinkUrl('');
                                                }
                                            }}
                                            className="link-input"
                                            autoFocus
                                        />
                                        <button
                                            className="link-action-btn"
                                            onClick={() => {
                                                if (linkUrl) {
                                                    editor?.chain().focus().setLink({ href: linkUrl }).run();
                                                    setShowLinkPopover(false);
                                                    setLinkUrl('');
                                                }
                                            }}
                                            title={t('drafterConfirm')}
                                        >
                                            ↵
                                        </button>
                                        <div className="link-divider" />
                                        <button
                                            className="link-action-btn"
                                            onClick={() => {
                                                if (linkUrl) window.open(linkUrl, '_blank');
                                            }}
                                            title={t('drafterOpenLink')}
                                            disabled={!linkUrl}
                                        >
                                            ⇗
                                        </button>
                                        <button
                                            className="link-action-btn delete"
                                            onClick={() => {
                                                editor?.chain().focus().unsetLink().run();
                                                setShowLinkPopover(false);
                                                setLinkUrl('');
                                            }}
                                            title={t('drafterDeleteLink')}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tiptap-divider" />

                        {/* Alignment Buttons */}
                        <button
                            className={`tiptap-btn ${editor?.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                            disabled={!editor}
                            title={t('drafterAlignLeft')}
                        >
                            <AlignLeft className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                            disabled={!editor}
                            title={t('drafterAlignCenter')}
                        >
                            <AlignCenter className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                            disabled={!editor}
                            title={t('drafterAlignRight')}
                        >
                            <AlignRight className="w-4 h-4" />
                        </button>
                        <button
                            className={`tiptap-btn ${editor?.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                            onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
                            disabled={!editor}
                            title={t('drafterAlignJustify')}
                        >
                            <AlignJustify className="w-4 h-4" />
                        </button>

                        {/* Right Toolbar Groups - Save/Download/Undo/Redo */}
                        <div className="tiptap-toolbar-right">
                            {/* Save Button */}
                            <button
                                className="tiptap-btn toolbar-action-btn save-btn"
                                onClick={saveCurrentSession}
                                title={t('drafterSave')}
                            >
                                <Save className="w-4 h-4" />
                            </button>

                            {/* Download Button */}
                            <div className="dropdown-container download-dropdown">
                                <button
                                    className={`tiptap-btn ${isExporting ? 'disabled' : ''}`}
                                    onClick={() => setShowExportMenu(!showExportMenu)}
                                    disabled={isExporting}
                                    title={t('drafterDownload')}
                                >
                                    {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                </button>
                                {showExportMenu && (
                                    <div className="dropdown-menu download-menu">
                                        <div
                                            className="dropdown-item"
                                            onClick={() => exportDocument('doc')}
                                        >
                                            <FileText className="w-4 h-4" />
                                            <span>{t('drafterExportWord')}</span>
                                        </div>
                                        <div
                                            className="dropdown-item"
                                            onClick={() => exportDocument('pdf')}
                                        >
                                            <FileIcon className="w-4 h-4" />
                                            <span>{t('drafterExportPdf')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="tiptap-divider" />

                            {/* Undo/Redo */}
                            <button
                                className={`tiptap-btn ${!editor?.can().undo() ? 'disabled' : ''}`}
                                onClick={() => editor?.chain().focus().undo().run()}
                                disabled={!editor?.can().undo()}
                                title={t('drafterUndo')}
                            >
                                <Undo2 className="w-4 h-4" />
                            </button>
                            <button
                                className={`tiptap-btn ${!editor?.can().redo() ? 'disabled' : ''}`}
                                onClick={() => editor?.chain().focus().redo().run()}
                                disabled={!editor?.can().redo()}
                                title={t('drafterRedo')}
                            >
                                <Redo2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Tiptap Editor Content - Notion Style */}
                    <div
                        className="editor-content notion-style"
                    >
                        {/* Notion-style block handles */}
                        <DragHandleWrapper editor={editor} />
                        <EditorContent editor={editor} />
                    </div>
                </div>

                {/* Right: AI Chat Panel */}
                <div className="chat-panel">
                    {/* Top Actions - New + History */}
                    <div className="chat-header-actions">
                        <button
                            className="chat-action-btn"
                            onClick={createNewSession}
                            title={t('drafterNewChat')}
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                        <div className="history-dropdown-container">
                            <button
                                className="chat-action-btn"
                                onClick={() => setShowHistoryMenu(!showHistoryMenu)}
                                title={t('drafterHistory')}
                            >
                                <History className="w-4 h-4" />
                            </button>
                            {showHistoryMenu && (
                                <div className="history-dropdown-menu">
                                    <div className="history-dropdown-header">{t('drafterHistoryTitle')}</div>
                                    {sessions.length === 0 ? (
                                        <div className="history-empty">{t('drafterNoHistory')}</div>
                                    ) : (
                                        sessions.slice(0, 10).map((session) => (
                                            <div
                                                key={session.id}
                                                className={`history-item ${currentSessionId === session.id ? 'active' : ''}`}
                                                onClick={() => loadSession(session.id)}
                                            >
                                                <span className="history-item-title">{session.title}</span>
                                                <span className="history-item-date">
                                                    {new Date(session.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="chat-messages" ref={chatContainerRef}>
                        {chatMessages.length === 0 ? (
                            <div className="chat-welcome">
                                <p>{t('drafterWelcome')}</p>
                                <p className="hint">{t('drafterYouCan')}</p>
                                <ul className="hint-list">
                                    <li>• {t('drafterUploadTemplate')}</li>
                                    <li>• {t('drafterDescribeNeeds')}</li>
                                    <li>• {t('drafterLetAiHelp')}</li>
                                </ul>
                                {/* Examples section removed */}
                            </div>
                        ) : (
                            <>
                                {/* Render all messages, support inline workflow */}
                                {chatMessages.map((msg) => (
                                    <React.Fragment key={msg.id}>
                                        {/* Show message as long as it has content or attachments */}
                                        {/* User message uses bubble, AI message shows content directly without bubble */}
                                        {(msg.content || msg.attachments) && (
                                            msg.role === 'user' ? (
                                                <div className={`chat-message user`}>
                                                    <div className="message-content">
                                                        {msg.content}
                                                        {/* Attachments shown as chips */}
                                                        {msg.attachments && msg.attachments.length > 0 && (
                                                            <div className="message-attachments">
                                                                {msg.attachments.map((att, idx) => (
                                                                    <span key={idx} className="message-attachment-chip">
                                                                        <Paperclip className="w-3 h-3" />
                                                                        {att.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="chat-message ai-no-bubble">
                                                    <div className="ai-content">
                                                        {msg.content}
                                                    </div>
                                                </div>
                                            )
                                        )}

                                        {/* If message contains workflow state, render workflow card */}
                                        {msg.workflowState && (
                                            <div className="workflow-message-container" style={{ marginTop: '8px', marginBottom: '16px', maxWidth: '100%' }}>
                                                <WorkflowStepCard
                                                    workflowId={msg.id}
                                                    steps={msg.workflowState.steps}
                                                    isRunning={msg.workflowState.isRunning}
                                                    reviewStatus={msg.workflowState.reviewStatus}
                                                    phaseInfo={{
                                                        phase: msg.workflowState.phase,
                                                        phaseLabel: msg.workflowState.phaseLabel,
                                                        activeWorkers: msg.workflowState.activeWorkers,
                                                        progress: msg.workflowState.progress,
                                                    }}
                                                    onAccept={() => {
                                                        // Update current message state to accepted
                                                        setChatMessages(prev => prev.map(m => {
                                                            if (m.id !== msg.id || !m.workflowState) return m;
                                                            return {
                                                                ...m,
                                                                workflowState: {
                                                                    ...m.workflowState,
                                                                    reviewStatus: 'accepted'
                                                                }
                                                            };
                                                        }));
                                                        // TODO: Trigger actual accepted callback (if any)
                                                    }}
                                                    onReject={() => {
                                                        // Reject change: rollback editor content
                                                        if (preWorkflowContent && editor) {
                                                            editor.commands.setContent(preWorkflowContent);
                                                            // Optional: add a hint message
                                                            // console.log('Rolled back editor content');
                                                        }

                                                        // Update current message state to rejected
                                                        setChatMessages(prev => prev.map(m => {
                                                            if (m.id !== msg.id || !m.workflowState) return m;
                                                            return {
                                                                ...m,
                                                                workflowState: {
                                                                    ...m.workflowState,
                                                                    reviewStatus: 'rejected'
                                                                }
                                                            };
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Loading state placeholder (only for last empty AI message) */}
                                        {msg.role === 'ai' && !msg.content && !msg.workflowState && isChatLoading && (
                                            <div className="chat-message ai-no-bubble">
                                                <div className="ai-content ai-thinking">{t('drafterThinking')}</div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </div>


                    {/* Input Area - Claude Style */}
                    <div className="chat-input-wrapper">
                        {/* Attachment preview area */}
                        {chatAttachments.length > 0 && (
                            <div className="chat-attachments-preview">
                                {chatAttachments.map((file, index) => (
                                    <div key={index} className="attachment-chip">
                                        <Paperclip className="w-3 h-3" />
                                        <span className="attachment-name">{file.name}</span>
                                        <button
                                            className="attachment-remove"
                                            onClick={() => removeAttachment(index)}
                                            title={t('drafterRemoveAttachment')}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div
                            className={`chat-input-box ${isDragOver ? 'drag-over' : ''}`}
                            onDragEnterCapture={(e) => {
                                e.preventDefault();
                                setIsDragOver(true);
                            }}
                            onDragLeaveCapture={(e) => {
                                e.preventDefault();
                                // Prevent dragleave when entering children
                                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                                setIsDragOver(false);
                            }}
                            onDragOverCapture={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = 'copy';
                                setIsDragOver(true);
                            }}
                            onDropCapture={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragOver(false);

                                let text = "";
                                let source = "unknown";
                                let internalRef: InternalContextReference | null = null;

                                // Try to get custom data
                                const highlightDataStr = e.dataTransfer.getData("application/x-lawhelper-highlight");
                                if (highlightDataStr) {
                                    try {
                                        const data = JSON.parse(highlightDataStr);
                                        text = data.text;
                                        source = "highlight";

                                        // Capture internal reference data
                                        if (data.origin === 'editor-internal' && data.from && data.to) {
                                            internalRef = {
                                                id: data.id,
                                                text: data.text,
                                                from: data.from,
                                                to: data.to,
                                                origin: 'editor-internal'
                                            };
                                        }
                                    } catch (err) {
                                        console.error("Failed to parse highlight drop data", err);
                                    }
                                }

                                // Fallback: try to get plain text
                                if (!text) {
                                    text = e.dataTransfer.getData("text/plain");
                                    source = "text";
                                }

                                if (text) {
                                    // Create a virtual file as reference
                                    // Truncate first 10 chars as part of filename to avoid it being too long
                                    const shortText = text.slice(0, 10).replace(/[\n\r]/g, ' ') + (text.length > 10 ? '...' : '');
                                    const fileName = `Ref: ${shortText}.txt`;
                                    const file = new File([text], fileName, { type: 'text/plain' });

                                    // Add to attachment list
                                    setChatAttachments(prev => [...prev, file]);

                                    // If internal reference, save to state for prompt construction
                                    if (internalRef) {
                                        setInternalReferences(prev => [...prev, internalRef!]);
                                        // Add visual hint (optional, currently reusing attachment chip)
                                    }
                                }
                            }}
                        >
                            {/* Hidden attachment upload input */}
                            <input
                                type="file"
                                ref={attachmentInputRef}
                                onChange={handleAttachmentUpload}
                                multiple
                                style={{ display: 'none' }}
                                accept=".pdf,.doc,.docx,.txt,.md,.json,.csv,.xls,.xlsx,.png,.jpg,.jpeg"
                            />
                            <div style={{ position: 'relative', flex: 1 }}>
                                <input
                                    ref={chatInputRef}
                                    type="text"
                                    placeholder={t('drafterInputPlaceholder')}
                                    value={chatInput}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setChatInput(val);
                                        // Detect @ trigger for mention autocomplete
                                        const cursorPos = e.target.selectionStart || val.length;
                                        const textBefore = val.substring(0, cursorPos);
                                        const atIdx = textBefore.lastIndexOf('@');
                                        if (atIdx >= 0 && (atIdx === 0 || textBefore[atIdx - 1] === ' ') && mentionFilesProp && mentionFilesProp.length > 0) {
                                            const query = textBefore.substring(atIdx + 1);
                                            if (!query.includes(' ') && !query.includes(']')) {
                                                setShowMentionMenu(true);
                                                setMentionQuery(query);
                                                setMentionIndex(0);
                                                return;
                                            }
                                        }
                                        setShowMentionMenu(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (showMentionMenu) {
                                            const filtered = (mentionFilesProp || []).filter(f =>
                                                f.name.toLowerCase().includes(mentionQuery.toLowerCase())
                                            ).slice(0, 8);
                                            if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, filtered.length - 1)); }
                                            else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); }
                                            else if (e.key === 'Enter' || e.key === 'Tab') {
                                                if (filtered.length > 0) {
                                                    e.preventDefault();
                                                    const item = filtered[mentionIndex];
                                                    const prefix = item.source === 'local' ? 'Local' : item.folderName;
                                                    const mention = `@[${prefix}/${item.name}]`;
                                                    const cursorPos = chatInputRef.current?.selectionStart || chatInput.length;
                                                    const atIdx = chatInput.substring(0, cursorPos).lastIndexOf('@');
                                                    setChatInput(chatInput.substring(0, atIdx) + mention + ' ' + chatInput.substring(cursorPos));
                                                    setShowMentionMenu(false);
                                                    return;
                                                }
                                            } else if (e.key === 'Escape') { setShowMentionMenu(false); }
                                        }
                                        if (e.key === 'Enter' && !showMentionMenu) sendChatMessage();
                                    }}
                                    disabled={isChatLoading || isGenerating || workflowRunning}
                                    className="chat-input"
                                />
                                {/* @Mention autocomplete dropdown */}
                                {showMentionMenu && mentionFilesProp && mentionFilesProp.length > 0 && (
                                    <div style={{
                                        position: 'absolute', bottom: '100%', left: 0, right: 0,
                                        maxHeight: 200, overflowY: 'auto', background: 'white', border: '1px solid #e2e8f0',
                                        borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, marginBottom: 4,
                                    }}>
                                        {(mentionFilesProp || [])
                                            .filter(f => f.name.toLowerCase().includes(mentionQuery.toLowerCase()))
                                            .slice(0, 8)
                                            .map((item, idx) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                                                        background: idx === mentionIndex ? '#f0f4ff' : 'transparent',
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    }}
                                                    onMouseEnter={() => setMentionIndex(idx)}
                                                    onClick={() => {
                                                        const prefix = item.source === 'local' ? 'Local' : item.folderName;
                                                        const mention = `@[${prefix}/${item.name}]`;
                                                        const cursorPos = chatInputRef.current?.selectionStart || chatInput.length;
                                                        const atIdx = chatInput.substring(0, cursorPos).lastIndexOf('@');
                                                        setChatInput(chatInput.substring(0, atIdx) + mention + ' ' + chatInput.substring(cursorPos));
                                                        setShowMentionMenu(false);
                                                        chatInputRef.current?.focus();
                                                    }}
                                                >
                                                    <span>{item.name}</span>
                                                    <span style={{ color: '#94a3b8', fontSize: 11 }}>{item.folderName}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                            <div className="chat-input-actions">
                                <span
                                    className={`workflow-hint ${isAgentMode ? 'active' : ''}`}
                                    onClick={() => setIsAgentMode(!isAgentMode)}
                                    style={{ cursor: 'pointer' }}
                                    title={isAgentMode ? t('drafterAgentModeOn') : t('drafterAgentModeOff')}
                                ><Bot className="w-4 h-4" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />Agent</span>
                                {/* Attachment button */}
                                <button
                                    className="input-action-btn attachment-btn"
                                    onClick={() => attachmentInputRef.current?.click()}
                                    title={t('drafterUploadAttachment')}
                                >
                                    <Paperclip className="w-4 h-4" />
                                </button>
                                <div className="input-actions-right">
                                    {workflowRunning ? (
                                        <button
                                            className="send-btn-circle stop-btn active"
                                            onClick={() => {
                                                // Cancel workflow
                                                if (workflowAbortController.current) {
                                                    workflowAbortController.current.abort();
                                                    workflowAbortController.current = null;
                                                }
                                                setWorkflowRunning(false);
                                                // Update status of all running workflow messages
                                                setChatMessages(prev => prev.map(msg => {
                                                    if (msg.workflowState?.isRunning) {
                                                        return {
                                                            ...msg,
                                                            workflowState: {
                                                                ...msg.workflowState,
                                                                isRunning: false,
                                                                steps: msg.workflowState.steps.map(s =>
                                                                    s.status === 'running' ? { ...s, status: 'completed' as const } : s
                                                                )
                                                            }
                                                        };
                                                    }
                                                    return msg;
                                                }));
                                            }}
                                            title={t('drafterStopGeneration')}
                                        >
                                            <Square className="w-3 h-3" fill="currentColor" />
                                        </button>
                                    ) : (
                                        <button
                                            className={`send-btn-circle ${chatInput.trim() && !isChatLoading && !isGenerating ? 'active' : ''}`}
                                            onClick={sendChatMessage}
                                            disabled={isChatLoading || isGenerating || !chatInput.trim()}
                                        >
                                            {isChatLoading || isGenerating ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <ArrowUp className="w-4 h-4" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RAG Panel Section */}
                {
                    isRagPanelVisible && ragPanelComponent && (
                        <div className="rag-panel-container">
                            {ragPanelComponent}
                        </div>
                    )
                }

                {/* Collapsed state: show open button on right */}
                {!isRagPanelVisible && onRagPanelToggle && (
                    <button
                        className="rag-open-btn"
                        onClick={onRagPanelToggle}
                        title={t('drafterOpenResource')}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                )}
            </div >

            {/* Styles */}
            < style > {`
            .legal-drafter-fullscreen {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #fdf2f8 100%);
                    z-index: 1000;
                    display: flex;
                    flex-direction: column;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .drafter-content {
                    display: grid;
                    flex: 1;
                    overflow: hidden;
                    gap: 1px;
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
                    padding: 12px;
                }

                .drafter-content.with-rag {
                    grid-template-columns: 50% 30% 20%;
                }

                .drafter-content.without-rag {
                    grid-template-columns: 60% 40%;
                }

                /* Editor Panel */
                .editor-panel {
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(20px);
                    border-radius: 16px;
                    border: 1px solid rgba(255, 255, 255, 0.8);
                    box-shadow: 
                        0 4px 6px -1px rgba(0, 0, 0, 0.05),
                        0 10px 15px -3px rgba(0, 0, 0, 0.08),
                        0 0 0 1px rgba(26, 26, 26, 0.05);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    margin-right: 6px;
                    position: relative;
                }

                /* Notion-style floating control buttons */
                .floating-controls {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    z-index: 10;
                    opacity: 0.6;
                    transition: opacity 0.2s ease;
                }

                .editor-panel:hover .floating-controls {
                    opacity: 1;
                }

                .file-info-compact {
                    font-size: 0.75rem;
                    color: #1A1A1A;
                    background: rgba(255, 255, 255, 0.95);
                    padding: 4px 10px;
                    border-radius: 6px;
                    border: 1px solid rgba(26, 26, 26, 0.15);
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .status-badge-compact {
                    font-size: 0.7rem;
                    color: #666666;
                    background: rgba(255, 255, 255, 0.95);
                    padding: 4px 8px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    animation: pulse 1.5s infinite;
                    border: 1px solid rgba(26, 26, 26, 0.2);
                }

                .floating-close-btn {
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: 8px;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 16px;
                    color: #64748b;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .floating-close-btn:hover {
                    background: #fee2e2;
                    border-color: #f87171;
                    color: #dc2626;
                    transform: scale(1.05);
                }

                /* Unified Panel Header */
                .panel-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    min-height: 60px;
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.08) 0%, rgba(51, 51, 51, 0.05) 100%);
                    border-bottom: 1px solid rgba(26, 26, 26, 0.1);
                }

                .panel-header h3 {
                    margin: 0;
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #1e1b4b;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .panel-header h3 svg {
                    color: #666666;
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .header-btn {
                    background: rgba(255, 255, 255, 0.8);
                    border: 1px solid rgba(26, 26, 26, 0.15);
                    border-radius: 10px;
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                    color: #475569;
                }

                .header-btn:hover {
                    background: rgba(26, 26, 26, 0.1);
                    border-color: rgba(26, 26, 26, 0.3);
                    transform: translateY(-1px);
                }

                .header-btn.rag-toggle {
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .header-btn.rag-toggle.active {
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.15) 0%, rgba(51, 51, 51, 0.15) 100%);
                    border-color: rgba(26, 26, 26, 0.4);
                    color: #1A1A1A;
                }

                .header-btn.close {
                    font-size: 20px;
                    padding: 4px 12px;
                    color: #64748b;
                    border-radius: 10px;
                }

                .header-btn.close:hover {
                    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
                    border-color: #f87171;
                    color: #dc2626;
                }

                .file-info {
                    font-size: 0.8rem;
                    color: #1A1A1A;
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.1) 0%, rgba(51, 51, 51, 0.1) 100%);
                    padding: 6px 12px;
                    border-radius: 8px;
                    border: 1px solid rgba(26, 26, 26, 0.2);
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .status-badge {
                    font-size: 0.75rem;
                    color: #666666;
                    background: rgba(26, 26, 26, 0.1);
                    padding: 4px 10px;
                    border-radius: 20px;
                    animation: pulse 1.5s infinite;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                }

                /* Tiptap template style toolbar */
                .editor-toolbar-tiptap {
                    display: flex;
                    align-items: center;
                    gap: 1px;
                    padding: 10px 12px;
                    background: #ffffff;
                    border-bottom: 1px solid #e5e7eb;
                    flex-wrap: nowrap;
                    overflow: visible;
                }

                .tiptap-btn {
                    background: transparent;
                    border: none;
                    border-radius: 4px;
                    padding: 6px;
                    cursor: pointer;
                    color: #6b7280;
                    transition: all 0.12s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 2px;
                    min-width: 28px;
                    height: 28px;
                }

                .tiptap-btn:hover:not(:disabled) {
                    background: #f3f4f6;
                    color: #374151;
                }

                .tiptap-btn.active {
                    background: #f3f4f6;
                    color: #111827;
                }

                .tiptap-btn:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }

                .tiptap-btn-text {
                    background: transparent;
                    border: none;
                    border-radius: 4px;
                    padding: 4px 8px;
                    cursor: pointer;
                    color: #374151;
                    font-size: 13px;
                    font-weight: 500;
                    transition: all 0.12s ease;
                    display: flex;
                    align-items: center;
                    gap: 3px;
                    height: 28px;
                }

                .tiptap-btn-text:hover {
                    background: #f3f4f6;
                    color: #111827;
                }

                .tiptap-btn-text:focus {
                    outline: none;
                    box-shadow: none;
                }

                .tiptap-btn-text.active {
                    background: #f3f4f6;
                    color: #111827;
                }

                .tiptap-divider {
                    width: 1px;
                    height: 24px;
                    background: #e5e7eb;
                    margin: 0 8px;
                    flex-shrink: 0;
                }

                .tiptap-toolbar-right {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    margin-left: auto;
                    padding-left: 8px;
                    border-left: 1px solid #e5e7eb;
                }

                /* Toolbar left actions */
                .toolbar-left-actions {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding-right: 8px;
                }

                .toolbar-action-btn {
                    transition: all 0.15s ease;
                }

                .toolbar-action-btn.exit-btn:hover {
                    color: #ef4444;
                    background: rgba(239, 68, 68, 0.1);
                }

                .toolbar-action-btn.new-btn:hover {
                    color: #10b981;
                    background: rgba(16, 185, 129, 0.1);
                }

                .toolbar-action-btn.save-btn:hover {
                    color: #3b82f6;
                    background: rgba(59, 130, 246, 0.1);
                }

                .toolbar-divider {
                    width: 1px;
                    height: 20px;
                    background: #e5e7eb;
                    margin-left: 4px;
                }

                /* Chat panel header actions - aligned height with editor toolbar */
                .chat-header-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 8px;
                    padding: 10px 12px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f8fafc;
                }

                .chat-action-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    border: none;
                    border-radius: 6px;
                    background: white;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                }

                .chat-action-btn:hover {
                    background: #f1f5f9;
                    color: #1e293b;
                }

                /* History Dropdown */
                .history-dropdown-container {
                    position: relative;
                }

                .history-dropdown-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 4px;
                    width: 280px;
                    max-height: 400px;
                    overflow-y: auto;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
                    border: 1px solid #e5e7eb;
                    z-index: 100;
                }

                .history-dropdown-header {
                    padding: 12px 16px;
                    font-weight: 600;
                    font-size: 13px;
                    color: #1e293b;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f8fafc;
                    border-radius: 12px 12px 0 0;
                }

                .history-empty {
                    padding: 24px 16px;
                    text-align: center;
                    color: #94a3b8;
                    font-size: 13px;
                }

                .history-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    padding: 12px 16px;
                    cursor: pointer;
                    border-bottom: 1px solid #f1f5f9;
                    transition: background 0.15s ease;
                }

                .history-item:hover {
                    background: #f8fafc;
                }

                .history-item.active {
                    background: #eff6ff;
                    border-left: 3px solid #3b82f6;
                }

                .history-item-title {
                    font-size: 13px;
                    font-weight: 500;
                    color: #1e293b;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .history-item-date {
                    font-size: 11px;
                    color: #94a3b8;
                }

                /* Keep legacy toolbar styles for compatibility */
                .toolbar-btn-compact {
                    background: transparent;
                    border: none;
                    border-radius: 6px;
                    padding: 6px 8px;
                    cursor: pointer;
                    font-size: 13px;
                    color: #64748b;
                    transition: all 0.15s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .toolbar-btn-compact:hover:not(:disabled) {
                    background: rgba(0, 0, 0, 0.05);
                    color: #1e293b;
                }

                .toolbar-btn-compact.active {
                    background: rgba(26, 26, 26, 0.12);
                    color: #1A1A1A;
                }

                .toolbar-btn-compact:disabled {
                    opacity: 0.35;
                    cursor: not-allowed;
                }

                .toolbar-btn-compact.loading {
                    color: #666666;
                }

                .toolbar-divider-compact {
                    width: 1px;
                    height: 16px;
                    background: rgba(0, 0, 0, 0.1);
                    margin: 0 6px;
                }

                /* Dropdown container */
                .dropdown-container {
                    position: relative;
                }

                .toolbar-btn-dropdown {
                    background: transparent;
                    border: none;
                    border-radius: 6px;
                    padding: 5px 8px;
                    cursor: pointer;
                    font-size: 13px;
                    color: #64748b;
                    transition: all 0.15s ease;
                    display: flex;
                    align-items: center;
                    gap: 2px;
                }

                .toolbar-btn-dropdown:hover {
                    background: rgba(0, 0, 0, 0.05);
                    color: #1e293b;
                }

                .toolbar-btn-dropdown.active {
                    background: rgba(26, 26, 26, 0.12);
                    color: #1A1A1A;
                }

                .dropdown-label {
                    font-weight: 600;
                    font-size: 13px;
                    color: inherit;
                }

                .dropdown-menu {
                    position: absolute;
                    top: calc(100% + 4px);
                    left: 0;
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    border-radius: 10px;
                    box-shadow: 
                        0 10px 40px rgba(0, 0, 0, 0.12),
                        0 2px 8px rgba(0, 0, 0, 0.06);
                    min-width: 140px;
                    z-index: 100;
                    overflow: hidden;
                    animation: dropdownSlide 0.15s ease-out;
                    padding: 4px 0;
                }

                .dropdown-menu.export-menu-position {
                    left: auto;
                    right: 0;
                }

                .dropdown-menu.font-menu {
                    min-width: 160px;
                    max-height: 320px;
                    overflow-y: auto;
                }

                /* Tiptap ListDropdownMenu style */
                .list-dropdown-menu {
                    min-width: auto !important;
                    padding: 4px !important;
                }

                .list-button-group {
                    display: flex;
                    gap: 2px;
                }

                .list-type-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    border: none;
                    background: transparent;
                    border-radius: 6px;
                    cursor: pointer;
                    color: #6b7280;
                    transition: all 0.15s ease;
                }

                .list-type-btn:hover {
                    background: rgba(26, 26, 26, 0.1);
                    color: #666666;
                }

                .list-type-btn.active {
                    background: rgba(26, 26, 26, 0.15);
                    color: #666666;
                }

                .list-type-btn:active {
                    transform: scale(0.95);
                }

                .font-label {
                    font-size: 12px;
                }

                @keyframes dropdownSlide {
                    from {
                        opacity: 0;
                        transform: translateY(-4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .dropdown-item {
                    padding: 8px 12px;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .dropdown-item svg {
                    color: #6b7280;
                }

                .dropdown-item:hover {
                    background: rgba(26, 26, 26, 0.08);
                    color: #1e1b4b;
                }

                .dropdown-item:hover svg {
                    color: #666666;
                }

                .dropdown-divider {
                    height: 1px;
                    background: rgba(0, 0, 0, 0.06);
                    margin: 4px 8px;
                }

                /* Link Popover */
                .link-popover {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 0;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
                    min-width: 320px;
                    z-index: 100;
                    animation: dropdownSlide 0.15s ease-out;
                    overflow: hidden;
                }

                .link-popover-header {
                    padding: 16px 20px;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                }

                .link-popover-header .link-icon {
                    color: #666666;
                }

                .link-popover-input-row {
                    display: flex;
                    align-items: center;
                    padding: 12px 16px;
                    gap: 8px;
                }

                .link-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    font-size: 14px;
                    color: #374151;
                    background: transparent;
                }

                .link-input::placeholder {
                    color: #9ca3af;
                }

                .link-action-btn {
                    background: transparent;
                    border: none;
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 16px;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                }

                .link-action-btn:hover:not(:disabled) {
                    background: #f3f4f6;
                    color: #374151;
                }

                .link-action-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .link-action-btn.delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .link-divider {
                    width: 1px;
                    height: 20px;
                    background: #e5e7eb;
                }

                /* Export Dropdown */
                .export-dropdown-container {
                    position: relative;
                }

                .toolbar-left, .toolbar-right {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .toolbar-btn {
                    background: rgba(255, 255, 255, 0.9);
                    border: 1px solid rgba(26, 26, 26, 0.12);
                    border-radius: 8px;
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 13px;
                    color: #475569;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 500;
                }

                .toolbar-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.1) 0%, rgba(51, 51, 51, 0.1) 100%);
                    border-color: rgba(26, 26, 26, 0.3);
                    color: #1A1A1A;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(26, 26, 26, 0.15);
                }

                .toolbar-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                /* Export Dropdown */
                .export-dropdown-container {
                    position: relative;
                }

                .toolbar-btn.export-btn.loading {
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.15) 0%, rgba(51, 51, 51, 0.15) 100%);
                    color: #1A1A1A;
                }

                .export-menu {
                    position: absolute;
                    top: calc(100% + 8px);
                    right: 0;
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(26, 26, 26, 0.15);
                    border-radius: 12px;
                    box-shadow: 
                        0 10px 40px rgba(26, 26, 26, 0.15),
                        0 4px 12px rgba(0, 0, 0, 0.08);
                    min-width: 200px;
                    z-index: 100;
                    overflow: hidden;
                    animation: slideDown 0.2s ease-out;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .export-menu-item {
                    padding: 12px 16px;
                    font-size: 13px;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 500;
                }

                .export-menu-item svg {
                    color: #666666;
                }

                .export-menu-item:hover {
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.08) 0%, rgba(51, 51, 51, 0.08) 100%);
                    color: #1e1b4b;
                }

                .export-menu-item:active {
                    background: rgba(26, 26, 26, 0.15);
                }

                .export-menu-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(26, 26, 26, 0.15), transparent);
                    margin: 4px 12px;
                }

                .toolbar-divider {
                    width: 1px;
                    height: 24px;
                    background: linear-gradient(180deg, transparent, rgba(26, 26, 26, 0.2), transparent);
                    margin: 0 10px;
                }

                /* Editor Content */
                .editor-content {
                    flex: 1;
                    overflow-y: auto;
                    position: relative;
                    background: #fff;
                }

                .editor-content.dragging {
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.05) 0%, rgba(51, 51, 51, 0.05) 100%);
                }

                .drop-overlay {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    gap: 12px;
                    background: linear-gradient(135deg, rgba(26, 26, 26, 0.1) 0%, rgba(51, 51, 51, 0.1) 100%);
                    border: 3px dashed #666666;
                    border-radius: 12px;
                    margin: 16px;
                    z-index: 10;
                    font-size: 1.1rem;
                    color: #1A1A1A;
                    font-weight: 500;
                }

                .tiptap-editor-content {
                    padding: 80px 120px 40px 120px;
                    min-height: 100%;
                    outline: none;
                    font-size: 15px;
                    line-height: 1.9;
                    color: #1e293b;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                .tiptap-editor-content p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: #a5b4c3;
                    pointer-events: none;
                    height: 0;
                    font-style: normal;
                }

                .tiptap-editor-content h1, .tiptap-editor-content h2, .tiptap-editor-content h3 {
                    margin-top: 1.2em;
                    margin-bottom: 0.6em;
                    font-weight: 600;
                    color: #1e1b4b;
                    font-family: 'Inter', sans-serif;
                }

                .tiptap-editor-content h1 {
                    font-size: 2em;
                }

                .tiptap-editor-content h2 {
                    font-size: 1.5em;
                }

                .tiptap-editor-content h3 {
                    font-size: 1.25em;
                }

                .tiptap-editor-content ul, .tiptap-editor-content ol {
                    padding-left: 1.5em;
                }

                /* Notion-style block editing */
                .editor-content.notion-style .ProseMirror > * {
                    position: relative;
                    padding-left: 4px;
                }

                .editor-content.notion-style .ProseMirror > *:hover {
                    /* Removed purple background to prevent unwanted highlighting */
                    border-radius: 4px;
                }

                /* Blockquote styling - Tiptap official style */
                .tiptap-editor-content blockquote {
                    border-left: 3px solid #0d0d0d;
                    padding-left: 1rem;
                    margin-left: 0;
                    margin-top: 1rem;
                    margin-bottom: 1rem;
                }

                /* Task list styling */
                .tiptap-editor-content ul[data-type="taskList"] {
                    list-style: none;
                    padding-left: 0;
                }

                .tiptap-editor-content ul[data-type="taskList"] li {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    margin: 4px 0;
                }

                .tiptap-editor-content ul[data-type="taskList"] li > label {
                    flex-shrink: 0;
                    user-select: none;
                    margin-top: 4px;
                }

                .tiptap-editor-content ul[data-type="taskList"] li > label input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                    accent-color: #666666;
                }

                .tiptap-editor-content ul[data-type="taskList"] li > div {
                    flex: 1;
                }

                .tiptap-editor-content ul[data-type="taskList"] li[data-checked="true"] > div {
                    text-decoration: line-through;
                    color: #9ca3af;
                }

                /* Code block styling */
                .tiptap-editor-content pre {
                    background: #1e1e1e;
                    color: #d4d4d4;
                    padding: 16px 20px;
                    border-radius: 8px;
                    font-family: 'Fira Code', 'Consolas', monospace;
                    font-size: 14px;
                    overflow-x: auto;
                }

                /* AI Chat Panel */
                .chat-panel {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 16px;
                    border: 1px solid rgba(255, 255, 255, 0.8);
                    box-shadow: 
                        0 4px 6px -1px rgba(0, 0, 0, 0.05),
                        0 10px 15px -3px rgba(0, 0, 0, 0.08),
                        0 0 0 1px rgba(26, 26, 26, 0.05);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    margin-right: 6px;
                    position: relative;
                }

                .chat-close-btn {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    width: 32px;
                    height: 32px;
                    background: rgba(254, 226, 226, 0.8);
                    border: 1px solid rgba(248, 113, 113, 0.3);
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 18px;
                    color: #dc2626;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                }

                .chat-close-btn:hover {
                    background: #fee2e2;
                    border-color: #f87171;
                    transform: scale(1.05);
                }

                .chat-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                    background: linear-gradient(180deg, rgba(26, 26, 26, 0.02) 0%, transparent 100%);
                }

                .chat-welcome {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #64748b;
                    text-align: center;
                    padding: 32px;
                }

                .chat-welcome > p:first-child {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1A1A1A;
                    margin-bottom: 8px;
                }

                .chat-welcome .hint {
                    font-size: 13px;
                    color: #666666;
                    margin: 20px 0 8px;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .hint-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    text-align: left;
                    width: 100%;
                    max-width: 280px;
                }

                .hint-list li {
                    font-size: 13px;
                    color: #64748b;
                    margin: 6px 0;
                    padding-left: 8px;
                }

                .hint-list.example {
                    margin-top: 8px;
                }

                .hint-list.example li {
                    padding: 14px 18px;
                    background: rgba(26, 26, 26, 0.03);
                    border: 1px solid rgba(26, 26, 26, 0.1);
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                    color: #1A1A1A;
                    font-weight: 500;
                    margin: 8px 0;
                }

                .hint-list.example li:hover {
                    background: rgba(26, 26, 26, 0.08);
                    border-color: rgba(26, 26, 26, 0.2);
                    transform: translateX(4px);
                    box-shadow: 0 4px 12px rgba(26, 26, 26, 0.1);
                }

                .chat-message {
                    display: flex;
                    width: 100%;
                    margin-bottom: 24px;
                    padding: 0 4px;
                }

                .chat-message.user {
                    justify-content: flex-end;
                }

                .chat-message.ai {
                    justify-content: flex-start;
                }

                .message-content {
                    max-width: 80%;
                    padding: 12px 16px;
                    font-size: 14px;
                    line-height: 1.6;
                    border-radius: 18px;
                    position: relative;
                    word-wrap: break-word;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
                }

                /* User Bubble - Right Side */
                /* User Bubble - Right Side */
                .chat-message.user .message-content {
                    background: #1A1A1A;
                    color: #FFFFFF;
                    border: 1px solid #1A1A1A;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); /* shadow-md */
                    border-radius: 18px 18px 4px 18px; /* Sharp bottom-right */
                }

                /* Message Attachment Styles */
                .message-attachments {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 8px;
                    padding-top: 8px;
                    border-top: 1px solid rgba(255, 255, 255, 0.2);
                }

                .message-attachment-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 10px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .message-attachment-chip svg {
                    opacity: 0.8;
                }

                /* AI Message - No Bubble, Left Aligned */
                .chat-message.ai-no-bubble {
                    justify-content: flex-start;
                }

                .chat-message.ai-no-bubble .ai-content {
                    max-width: 100%;
                    padding: 8px 4px;
                    font-size: 14px;
                    line-height: 1.7;
                    color: #1e293b;
                    background: transparent;
                    border: none;
                    box-shadow: none;
                }

                .chat-message.ai-no-bubble .ai-content.ai-thinking {
                    color: #64748b;
                    font-style: italic;
                }

                /* Legacy AI Bubble - Left Side (kept for compatibility) */
                .chat-message.ai .message-content {
                    background: #f8fafc; /* Slate-50 */
                    color: #1e293b; /* Slate-800 */
                    border: 1px solid rgba(226, 232, 240, 0.8);
                    border-radius: 18px 18px 18px 4px; /* Sharp bottom-left */
                }

                /* Workflow message content override (if any text content is in workflow message) */
                .chat-message.workflow .message-content {
                    background: transparent;
                    border: none;
                    box-shadow: none;
                    padding: 0;
                    margin-bottom: 8px;
                }

                /* Workflow button active state */
                .workflow-hint.active {
                    background: #1A1A1A !important;
                    color: white !important;
                    font-weight: 500;
                }

                .send-btn-circle.workflow-mode.active {
                    background: #1A1A1A !important;
                }

                /* Claude-style Input Area */
                .chat-input-wrapper {
                    padding: 12px 16px;
                    background: rgba(255, 255, 255, 0.98);
                    border-top: 1px solid rgba(0, 0, 0, 0.06);
                }

                .chat-input-box {
                    background: #f8fafc;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    border-radius: 16px;
                    overflow: hidden;
                }

                .chat-input {
                    width: 100%;
                    padding: 14px 16px 8px;
                    border: none;
                    font-size: 14px;
                    color: #1e293b;
                    background: transparent;
                    resize: none;
                    outline: none;
                }

                .chat-input::placeholder {
                    color: #9ca3af;
                }

                .chat-input-actions {
                    display: flex;
                    align-items: center;
                    padding: 8px 12px 10px;
                    gap: 8px;
                }

                .input-action-btn {
                    background: transparent;
                    border: none;
                    padding: 6px;
                    border-radius: 6px;
                    cursor: pointer;
                    color: #9ca3af;
                    transition: all 0.15s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .input-action-btn:hover:not(:disabled) {
                    background: rgba(0, 0, 0, 0.05);
                    color: #64748b;
                }

                .input-action-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .workflow-hint {
                    font-size: 12px;
                    color: #9ca3af;
                    padding: 4px 10px;
                    background: rgba(0, 0, 0, 0.03);
                    border-radius: 12px;
                }

                .input-actions-right {
                    margin-left: auto;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Attachment Preview Area */
                .chat-attachments-preview {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    padding: 8px 12px 4px;
                    background: rgba(26, 26, 26, 0.03);
                    border-radius: 12px 12px 0 0;
                    margin-bottom: -4px;
                }

                .chat-input-box.drag-over {
                    border-color: #1A1A1A;
                    background: rgba(26, 26, 26, 0.05);
                    box-shadow: 0 0 0 2px rgba(26, 26, 26, 0.2);
                }

                .attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 8px;
                    background: rgba(26, 26, 26, 0.08);
                    border: 1px solid rgba(26, 26, 26, 0.15);
                    border-radius: 8px;
                    font-size: 12px;
                    color: #1A1A1A;
                }

                .attachment-name {
                    max-width: 120px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .attachment-remove {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0 2px;
                    font-size: 14px;
                    line-height: 1;
                    transition: color 0.15s;
                }

                .attachment-remove:hover {
                    color: #ef4444;
                }

                .attachment-btn:hover {
                    color: #1A1A1A !important;
                }

                .send-btn-circle {
                    width: 32px;
                    height: 32px;
                    padding: 0;
                    background: rgba(0, 0, 0, 0.06);
                    border: none;
                    border-radius: 50%;
                    color: #9ca3af;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .send-btn-circle.active {
                    background: #1e293b;
                    color: #fff;
                }

                .send-btn-circle:hover:not(:disabled).active {
                    background: #0f172a;
                    transform: scale(1.05);
                }

                .send-btn-circle:disabled {
                    cursor: not-allowed;
                }

                .send-btn-circle.stop-btn {
                    background: #ef4444;
                    color: #fff;
                }

                .send-btn-circle.stop-btn:hover {
                    background: #dc2626;
                    transform: scale(1.05);
                }

                /* RAG Panel Container */
                .rag-panel-container {
                    position: relative;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 16px;
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    box-shadow: 
                        0 4px 6px -1px rgba(0, 0, 0, 0.05),
                        0 10px 15px -3px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .rag-inner-close {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    color: #64748b;
                    border-radius: 6px;
                    cursor: pointer;
                    z-index: 100;
                    transition: all 0.2s ease;
                }

                .rag-inner-close:hover {
                    background: #f1f5f9;
                    color: #334155;
                }

                /* RAG Open Button - shown when panel is collapsed */
                .rag-open-btn {
                    position: absolute;
                    top: 50%;
                    right: 12px;
                    transform: translateY(-50%);
                    width: 24px;
                    height: 56px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #374151, #1f2937);
                    border: none;
                    border-radius: 8px;
                    color: #FFFFFF;
                    cursor: pointer;
                    z-index: 100;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 6px rgba(31,41,55,0.35);
                }

                .rag-open-btn:hover {
                    background: linear-gradient(135deg, #1f2937, #111827);
                    box-shadow: 0 3px 12px rgba(17,24,39,0.45);
                }

                /* Make drafter-content support relative positioning */
                .drafter-content {
                    position: relative;
                }

                /* Custom Scrollbar */
                .chat-messages::-webkit-scrollbar,
                .editor-content::-webkit-scrollbar {
                    width: 6px;
                }

                .chat-messages::-webkit-scrollbar-track,
                .editor-content::-webkit-scrollbar-track {
                    background: transparent;
                }

                .chat-messages::-webkit-scrollbar-thumb,
                .editor-content::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.1);
                    border-radius: 3px;
                }

                .chat-messages::-webkit-scrollbar-thumb:hover,
                .editor-content::-webkit-scrollbar-thumb:hover {
                    background: rgba(0, 0, 0, 0.2);
                }
            `}</style >
        </div >
    );
};

export default LegalDrafter;
